import type { CoachCadence } from '@/types';

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export const timezoneAbbreviation = (
  timezone: string,
  date = new Date(),
): string => {
  try {
    // 'shortGeneric' yields DST-agnostic abbreviations (e.g. "PT", "ET")
    // to match the Figma design, rather than 'short' ("PST"/"PDT").
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortGeneric',
    }).formatToParts(date);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
};

const formatTime = (time: string): string => {
  const [h = '0', m = '0'] = time.split(':');
  const hour = Number(h);
  const suffix = hour >= 12 ? 'pm' : 'am';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${m}${suffix}`;
};

const formatMonthYear = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', {
    month: 'long',
    timeZone: 'UTC',
    year: 'numeric',
  });

// Maps a backend `frequency` to a singular unit. Interval is expressed in
// `intervalWeeks` for weekly cadences; other frequencies use the interval as a
// count of their own unit. Weekly is the only cadence in use today, but this
// keeps the label correct if daily/monthly cadences are introduced.
const FREQUENCY_UNIT: Record<string, string> = {
  daily: 'day',
  monthly: 'month',
  weekly: 'week',
  yearly: 'year',
};

const intervalLabel = (cadence: CoachCadence): string => {
  const unit = FREQUENCY_UNIT[cadence.frequency] ?? 'week';
  const count = cadence.intervalWeeks;
  return count === 1 ? unit : `${count} ${unit}s`;
};

export const formatCadenceLine = (cadence: CoachCadence): string => {
  if (
    !cadence.hasUpcoming ||
    cadence.weekday === null ||
    cadence.timeOfDay === null
  ) {
    return 'No upcoming sessions scheduled';
  }
  const day = WEEKDAYS[cadence.weekday] ?? '';
  const tz = timezoneAbbreviation(cadence.timezone);
  const meeting = `Meeting every ${intervalLabel(cadence)} on ${day} at ${formatTime(cadence.timeOfDay)}${tz ? ` ${tz}` : ''}`;
  const end =
    cadence.contractEnd?.displayMode === 'contract_end' &&
    cadence.contractEnd.date
      ? ` • Until ${formatMonthYear(cadence.contractEnd.date)}`
      : ' • Ongoing';
  return `${meeting}${end}`;
};
